import {
  BoltIcon,
  ArrowDownTrayIcon,
  BookOpenIcon,
  UsersIcon,
  CalendarDaysIcon,
  PlayIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { SidebarSection } from '../../../shared/types/navigation'
import { downloadIntroductionMaterial } from '../../../shared/utils/pdfDownload.helper'
import type { DashboardActionBox } from '../../../apiLayer/types/dashboard'
import { DashboardActionBoxType } from '../../../apiLayer/types/dashboard'
import { FacilitatorRoutes } from '../../../config/routes/routeEnums'

export interface ActionBox {
  type: string
  title: string
  description: string
  buttonText: string
}

export interface InfoBox {
  title: string
  value: number | string
  change?: string
  status?: string
}

/**
 * Get the appropriate icon component for action box type
 */
export const getActionBoxIcon = (type: DashboardActionBoxType) => {
  switch (type) {
    case DashboardActionBoxType.SETUP_TRAINING_SPRINT:
      return BoltIcon
    case DashboardActionBoxType.GENERATE_REPORTS:
      return ArrowDownTrayIcon
    case DashboardActionBoxType.DOWNLOAD_INTRODUCTION_MATERIAL:
      return BookOpenIcon
    default:
      return BoltIcon
  }
}

/**
 * Get the appropriate click handler for action box type
 */
export const getActionBoxClickHandler = (
  type: DashboardActionBoxType,
  setActiveSection: (section: SidebarSection) => void,
  navigate: (path: string) => void
) => {
  switch (type) {
    case DashboardActionBoxType.SETUP_TRAINING_SPRINT:
      return () => {
        setActiveSection(SidebarSection.SPRINT_CREATION)
        navigate('/facilitator/sprint-creation')
      }
    case DashboardActionBoxType.GENERATE_REPORTS:
      return () => {
        setActiveSection(SidebarSection.DOWNLOAD_REPORTS)
        navigate(FacilitatorRoutes.GENERATE_REPORT)
      }
    case DashboardActionBoxType.DOWNLOAD_INTRODUCTION_MATERIAL:
      return async () => {
        try {
          await downloadIntroductionMaterial()
        } catch (error) {
          // TODO: Show user-friendly error message
        }
      }
    default:
      return () => {}
  }
}

/**
 * Get the appropriate icon component for info box based on title
 */
export const getInfoBoxIcon = (title: string) => {
  const titleLower = title.toLowerCase()
  if (titleLower.includes('sprint')) return UsersIcon
  if (titleLower.includes('session')) return CalendarDaysIcon
  if (titleLower.includes('game')) return PlayIcon
  if (titleLower.includes('participant')) return UserGroupIcon
  return UsersIcon
}

/**
 * Parse change data from info box change string
 */
export const parseChangeData = (change?: string, status?: string) => {
  if (change) {
    const isIncrease = change.includes('↑')
    const isDecrease = change.includes('↓')
    const percentMatch = change.match(/(\d+)%/)
    const percent = percentMatch ? parseInt(percentMatch[1]) : 0

    return {
      percentChange: isIncrease ? percent : isDecrease ? -percent : 0,
      changeText: change.replace(/[↑↓]\s*\d+%\s*/, '')
    }
  }
  return { percentChange: 0, changeText: status || '' }
}

// Static action boxes for Facilitator Dashboard (non-API)
export const FACILITATOR_ACTION_BOXES: DashboardActionBox[] = [
  {
    type: DashboardActionBoxType.SETUP_TRAINING_SPRINT,
    title: 'facilitator.dashboard.boxes.setupTrainingSprint.title',
    description: 'facilitator.dashboard.boxes.setupTrainingSprint.description',
    buttonText: 'facilitator.dashboard.boxes.setupTrainingSprint.button'
  },
  {
    type: DashboardActionBoxType.GENERATE_REPORTS,
    title: 'facilitator.dashboard.boxes.generateReports.title',
    description: 'facilitator.dashboard.boxes.generateReports.description',
    buttonText: 'facilitator.dashboard.boxes.generateReports.button'
  },
  {
    type: DashboardActionBoxType.DOWNLOAD_INTRODUCTION_MATERIAL,
    title: 'facilitator.dashboard.boxes.downloadMaterial.title',
    description: 'facilitator.dashboard.boxes.downloadMaterial.description',
    buttonText: 'facilitator.dashboard.boxes.downloadMaterial.button'
  }
]

/**
 * Info box configurations
 * Defines structure and translation keys
 * Values injected at runtime from real data or mocks
 */
export const FACILITATOR_INFO_BOX_CONFIG = [
  {
    key: 'activeSprints',
    title: 'facilitator.dashboard.infoBoxes.activeSprints',
    status: 'facilitator.dashboard.infoBoxes.status.fromLastMonth',
    hasPercentChange: true
  },
  {
    key: 'upcomingSessions',
    title: 'facilitator.dashboard.infoBoxes.upcomingSessions',
    status: 'facilitator.dashboard.infoBoxes.status.fromYesterday',
    hasPercentChange: true
  },
  {
    key: 'gameSessionsLive',
    title: 'facilitator.dashboard.infoBoxes.gameSessionsLive',
    statusLive: 'facilitator.dashboard.infoBoxes.status.liveNow',
    statusInactive: 'facilitator.dashboard.infoBoxes.status.noLiveSessions',
    hasPercentChange: false
  },
  {
    key: 'totalParticipants',
    title: 'facilitator.dashboard.infoBoxes.totalParticipants',
    status: 'facilitator.dashboard.infoBoxes.status.fromLastMonth',
    hasPercentChange: true
  }
] as const
